import { toSubmitDate } from "./tkClients";

/**
 * @typedef {{ entity: "person"|"engagement", field: string }} ReferralDestination
 * @typedef {{
 *   originalFieldKey: string,
 *   label: string,
 *   extractedValue: unknown,
 *   reviewedValue: unknown,
 *   destination: ReferralDestination|null,
 *   used: boolean,
 *   sourceSection: string|null,
 *   metadata: Record<string, unknown>|null
 * }} ReferralExtractionField
 * @typedef {{
 *   first_name: string,
 *   last_name: string,
 *   preferred_name: string,
 *   date_of_birth: string,
 *   gender_id: string|null,
 *   ethnicity_id: string|null,
 *   iwi_id: string|null,
 *   nhi: string,
 *   phone: string,
 *   phone_numbers: Array<{phone_type: string, phone_number: string}>,
 *   email: string,
 *   address_line_1: string,
 *   suburb: string,
 *   city: string,
 *   postcode: string
 * }} ReferralPersonDraft
 */

const PERSON_FIELDS = Object.freeze([
    "first_name",
    "last_name",
    "preferred_name",
    "date_of_birth",
    "gender_id",
    "ethnicity_id",
    "iwi_id",
    "nhi",
    "phone",
    "phone_numbers",
    "email",
    "address_line_1",
    "suburb",
    "city",
    "postcode",
]);

const ENGAGEMENT_DRAFT_FIELDS = Object.freeze({
    service_code: "serviceCode",
    referral_source: "referralSource",
    risk_factor: "riskFactor",
    entered_at: "enteredDate",
});

const pad = (value) => String(value).padStart(2, "0");

const toDisplayDate = (value) => {
    if (value == null || value === "") return "";
    const text = String(value).trim();
    const displayMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
    if (displayMatch) return text;
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;

    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const toIsoDate = (value) => {
    if (value == null || value === "") return null;
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value).trim());
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
        date.getUTCFullYear() !== year
        || date.getUTCMonth() + 1 !== month
        || date.getUTCDate() !== day
    ) {
        return null;
    }
    return `${match[3]}-${match[2]}-${match[1]}`;
};

const normalizedMappedValue = (field) => {
    const value = field.reviewedValue;
    if (field.destination?.field === "date_of_birth" || field.destination?.field === "entered_at") {
        return toDisplayDate(value);
    }
    return value;
};

const hasDraftValue = (value) => value !== null && value !== undefined && value !== "";

/** @returns {ReferralPersonDraft} */
export const createEmptyPersonDraft = () => ({
    first_name: "",
    last_name: "",
    preferred_name: "",
    date_of_birth: "",
    gender_id: null,
    ethnicity_id: null,
    iwi_id: null,
    nhi: "",
    phone: "",
    phone_numbers: [],
    email: "",
    address_line_1: "",
    suburb: "",
    city: "",
    postcode: "",
});

/**
 * @param {ReferralPersonDraft} personDraft
 * @param {ReferralExtractionField[]} fields
 * @returns {ReferralPersonDraft}
 */
export const applyPersonExtraction = (personDraft, fields) => (
    fields
        .filter(field => field.destination?.entity === "person")
        .reduce((draft, field) => {
            const value = normalizedMappedValue(field);
            return hasDraftValue(value)
                ? { ...draft, [field.destination.field]: value }
                : draft;
        }, { ...personDraft })
);

export const applyEngagementExtraction = (engagementDraft, fields) => (
    fields
        .filter(field => field.destination?.entity === "engagement")
        .reduce((draft, field) => {
            const draftField = ENGAGEMENT_DRAFT_FIELDS[field.destination.field];
            const value = normalizedMappedValue(field);
            return draftField && hasDraftValue(value)
                ? { ...draft, [draftField]: value }
                : draft;
        }, { ...engagementDraft })
);

export const toPersonPayload = (personDraft) => {
    const legacyPhone = String(personDraft.phone || "").trim();
    const phoneNumbers = Array.isArray(personDraft.phone_numbers)
        ? personDraft.phone_numbers.map(phone => ({
            phone_type: String(phone?.phone_type || "").trim(),
            phone_number: String(phone?.phone_number || "").trim(),
        }))
        : [];
    const normalizedPhoneNumbers = phoneNumbers.length
        ? phoneNumbers
        : legacyPhone
            ? [{ phone_type: "Other", phone_number: legacyPhone }]
            : [];

    return PERSON_FIELDS.reduce((payload, field) => {
        let value;
        if (field === "date_of_birth") {
            value = toIsoDate(personDraft[field]);
        } else if (field === "phone") {
            value = normalizedPhoneNumbers[0]?.phone_number || null;
        } else if (field === "phone_numbers") {
            value = normalizedPhoneNumbers;
        } else {
            value = typeof personDraft[field] === "string"
                ? personDraft[field].trim() || null
                : personDraft[field] ?? null;
        }
        return { ...payload, [field]: value };
    }, {});
};

export const mappedPersonFields = (fields) => (
    fields.filter(field => field.destination?.entity === "person" && hasDraftValue(field.reviewedValue))
);

/**
 * @param {ReferralExtractionField[]} fields
 * @returns {ReferralExtractionField[]}
 */
export const hasReferralValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return Boolean(value.trim());
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
};

export const referralSpecificInformationFields = (fields) => (
    fields.filter(field => !field.used && hasReferralValue(field.reviewedValue))
);

/**
 * @param {ReferralExtractionField[]} fields
 * @param {string} fieldKey
 * @param {unknown} reviewedValue
 * @returns {ReferralExtractionField[]}
 */
export const updateReviewedField = (fields, fieldKey, reviewedValue) => (
    fields.map(field => (
        field.originalFieldKey === fieldKey
            ? { ...field, reviewedValue }
            : field
    ))
);

export const displayReferralValue = (value) => {
    if (value === null || value === undefined || value === "") return "Not provided";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
};

export const valuesDiffer = (storedValue, extractedValue, fieldName) => {
    const stored = fieldName === "date_of_birth"
        ? toDisplayDate(storedValue)
        : storedValue;
    return String(stored ?? "").trim().toLowerCase()
        !== String(extractedValue ?? "").trim().toLowerCase();
};

export const collectReferralWorkflowErrors = (workflow) => {
    const errors = [];
    if (!["extract", "none"].includes(workflow.referralMode)) {
        errors.push({ step: 2, message: "Choose whether to use a referral document." });
    }
    if (workflow.referralMode === "extract" && !workflow.acceptedReferral) {
        errors.push({ step: 2, message: "Extract and review the referral document before continuing." });
    }
    if (workflow.personMode === "existing" && !workflow.selectedPerson?.id) {
        errors.push({ step: 1, message: "Select an existing Te Kohinga person." });
    }
    if (workflow.personMode === "new") {
        if (!String(workflow.personDraft.first_name || "").trim()) {
            errors.push({ step: 3, message: "Enter a first name for the new person." });
        }
        if (
            workflow.personDraft.date_of_birth
            && !toPersonPayload(workflow.personDraft).date_of_birth
        ) {
            errors.push({ step: 3, message: "Enter the date of birth as DD/MM/YYYY." });
        }
    }
    if (!workflow.engagementDraft.serviceCode) {
        errors.push({ step: 4, message: "Select a Client Engagement service." });
    }
    if (!toSubmitDate(workflow.engagementDraft.enteredDate)) {
        errors.push({ step: 4, message: "Enter a valid Client Engagement entry date." });
    }
    return errors;
};

const createChecklistPayload = (engagementDraft) => {
    const definitions = engagementDraft.checklistDefinitionIds.map(id => ({
        checklist_item_definition_id: id,
    }));
    const customItems = engagementDraft.customChecklistLabels.map(label => ({
        custom_label: label,
    }));
    return [...definitions, ...customItems];
};

export const createReferralApprovalPayload = (workflow) => {
    const personChoice = workflow.personMode === "existing"
        ? { mode: "existing", personId: workflow.selectedPerson.id }
        : { mode: "new", person: toPersonPayload(workflow.personDraft) };
    const extraction = workflow.acceptedReferral
        ? {
            definitionId: workflow.acceptedReferral.definitionId,
            definitionUpdatedAt: workflow.acceptedReferral.definitionUpdatedAt,
            formType: workflow.acceptedReferral.formType,
            fields: workflow.extractionFields.map(field => ({
                originalFieldKey: field.originalFieldKey,
                extractedValue: field.extractedValue,
                reviewedValue: field.reviewedValue,
                metadata: field.metadata ? { ...field.metadata } : null,
            })),
        }
        : null;

    return {
        approved: true,
        personChoice,
        engagement: {
            service_code: workflow.engagementDraft.serviceCode,
            assigned_worker: workflow.engagementDraft.assignedWorker,
            additional_workers: workflow.engagementDraft.additionalWorkers,
            referral_source: workflow.engagementDraft.referralSource,
            needs_tier: workflow.engagementDraft.riskFactor,
            entered_at: toSubmitDate(workflow.engagementDraft.enteredDate),
            checklist_items: createChecklistPayload(workflow.engagementDraft),
        },
        extraction,
    };
};
